import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import useToast from "../../components/common/useToast";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, formatDateTime } from "../../utils/HelperFunctions";
import "../../assets/css/datatable.css";
import BazarProduct from "../../models/bazarProduct";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { Dropdown } from "primereact/dropdown";
import { Skeleton } from "primereact/skeleton";
import { ChartBarSquareIcon } from "@heroicons/react/24/outline";
import CountryFlag from "../../components/common/CountryFlag";

const NewLeadsPage: React.FC = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const navigate = useNavigate();
    const [newLeadsData, setNewLeadsData] = useState<any[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);
    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [skipData, setSkipData] = useState(0);
    const [totalRecords, settotalRecords] = useState(0);
    const [totalPages, settotalPages] = useState<any>(0);
    const { showToast }: any = useToast();
    const [name, setName] = useState<string>("");
    const [sortField, setSortField] = useState<string>("createddate");
    const [sortOrder, setSortOrder] = useState<any>(-1);

    const startIndex = (currentPage - 1) * itemsPerPage;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const ipBodyTemplate = (rowData: any) => rowData.ip ? (
        <span style={{ display: "inline-flex", alignItems: "center" }}>
            <CountryFlag countryCode={rowData.ipcountry} />
            <span style={{ fontFamily: "monospace" }}>{rowData.ip}</span>
        </span>
    ) : "-";

    const lastActiveBodyTemplate = (rowData: any) => rowData.lastactiveago ? (
        <div>
            <div>{rowData.lastactiveago}</div>
            {rowData.lastactivecity && (
                <div className="text-xs" style={{ color: "#9ca3af", display: "flex", alignItems: "center" }}>
                    <CountryFlag countryCode={rowData.lastactiveipcountry} size={11} />
                    {rowData.lastactivecity}
                </div>
            )}
        </div>
    ) : "-";

    const actionBodyTemplate = (rowData: BazarProduct) => {
        return (
            <div className="action-icons">
                <Button label="View" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleEdit(rowData)} />
                {/* <Button label="Remove" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleDelete(rowData)} /> */}
            </div>
        );
    };

    async function getNewLeadsData() {
        try {
            setDataLoad(true);

            const page = currentPage + 1;

            const params: any = {
                page,
                limit: itemsPerPage,
                sortField,
                sortOrder: sortOrder === 1 ? "asc" : "desc",
            };
            if (name) params.search = name;

            const response = await apiFunctions.get("/user/new-leads", { params });

            if (response.data.success) {
                setNewLeadsData(response.data.data);
                settotalRecords(response.data.totalRecords);
                settotalPages(response.data.totalPages);
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response.data.message,
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setDataLoad(false);
            setLoad(false);
        }
    }

    const handleEdit = (rowData: any) => {
        navigate(`/admin/profiles/view/${rowData._id}`);
    };

    const onSort = (e: any) => {
        setSortOrder(e.sortOrder);
        setSortField(e.sortField);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const getsearchdata = (e: any) => {
        if (e.target.value.length >= 2) {
            setName(e.target.value);
            setCurrentPage(0);
        } else {
            setName("");
            setLoad(false);
        }
    };

    useEffect(() => {
        setSkipData(currentPage * itemsPerPage);
    }, [currentPage, itemsPerPage]);

    useEffect(() => {
        if (!load) {
            getNewLeadsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipData, itemsPerPage, sortField, sortOrder, name]);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getNewLeadsData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const header = (
        <>
            <div className="grid grid-cols-12 gap-4 items-center table-header">
                {/* Title */}
                <div className="col-span-12 sm:col-span-6">
                    <h2 className="card-title m-0 text-lg sm:text-xl">New Leads</h2>
                </div>

                {/* Search */}
                <div className="col-span-12 sm:col-span-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
                    <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                        <span className="text-gray-600">Search:</span>
                        <PrimeInputText
                            type="text"
                            className="search-input w-full sm:w-auto"
                            placeholder="Search by name, lastname or city..."
                            onChange={(e) => getsearchdata(e)}
                        />
                    </div>
                </div>
            </div>
        </>
    );

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    return (
        <>
            <div className="dashboard-margin">
                {isMobile ? (
                    <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                        <div className="p-3 pb-2">{header}</div>
                        <div className="flex flex-col gap-2 px-3 pb-3">
                            {dataLoad ? (
                                Array.from({ length: itemsPerPage }, (_, i) => (
                                    <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <Skeleton width="60%" height="1rem" className="mb-2" />
                                        <Skeleton width="40%" height="0.8rem" />
                                    </div>
                                ))
                            ) : newLeadsData.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "64px 0" }}>
                                    <ChartBarSquareIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                    <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No leads found</p>
                                </div>
                            ) : (
                                newLeadsData.map((rowData: any, idx: number) => (
                                    <div key={rowData._id || idx} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>{rowData.name} {rowData.surname}</p>
                                        <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                            <div><span className="font-medium">District:</span> {rowData.district}</div>
                                            <div>{formatDateTime(rowData.createddate)}</div>
                                            {rowData.ip && (
                                                <div className="flex items-center">
                                                    <span className="font-medium">IP:&nbsp;</span>
                                                    <CountryFlag countryCode={rowData.ipcountry} size={12} />
                                                    <span style={{ fontFamily: "monospace" }}>{rowData.ip}</span>
                                                </div>
                                            )}
                                            {rowData.lastactiveago && (
                                                <div className="flex items-center">
                                                    <span className="font-medium">Last Active:&nbsp;</span>
                                                    {rowData.lastactiveago}
                                                    {rowData.lastactivecity && (
                                                        <>
                                                            &nbsp;·&nbsp;
                                                            <CountryFlag countryCode={rowData.lastactiveipcountry} size={12} />
                                                            {rowData.lastactivecity}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex justify-end gap-2 mt-2">
                                            {actionBodyTemplate(rowData)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    <DataTable
                        scrollable
                        scrollHeight="calc(100vh - 260px)"
                        style={{ minHeight: "calc(100vh - 185px)", background: "#fff" }}
                        header={header}
                        value={
                            dataLoad
                                ? Array.from({ length: itemsPerPage }, (_, i) => ({ _skeletonId: i }))
                                : newLeadsData
                        }
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={onSort}
                        resizableColumns
                        removableSort
                        rows={itemsPerPage}
                        emptyMessage={
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <ChartBarSquareIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No leads found</p>
                            </div>
                        }
                    >
                        <Column field="name" header="Name" sortable body={(rowData) => dataLoad ? <Skeleton /> : rowData.name} />
                        <Column field="surname" header="Last Name" sortable body={(rowData) => dataLoad ? <Skeleton /> : rowData.surname} />
                        <Column field="createddate" header="Registration Date Time" sortable
                            body={(rowData) =>
                                dataLoad ? <Skeleton /> : formatDateTime(rowData.createddate)
                            }
                        />
                        <Column field="district" header="District" sortable body={(rowData) => dataLoad ? <Skeleton /> : rowData.district} />
                        <Column field="ip" header="IP" body={(rowData) => dataLoad ? <Skeleton /> : ipBodyTemplate(rowData)} />
                        <Column header="Last Active" body={(rowData) => dataLoad ? <Skeleton /> : lastActiveBodyTemplate(rowData)} />
                        <Column header="Action" className="action-column" frozen body={(rowData) => dataLoad ? <Skeleton /> : actionBodyTemplate(rowData)} />
                    </DataTable>
                )}
                {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    {/* PrimeReact Paginator */}
                    <Paginator
                        template={paginatoreTemplate}
                        first={startIndex}
                        rows={itemsPerPage}
                        totalRecords={totalRecords}
                    />

                    {/* Custom Pagination Buttons */}
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage === 0}
                        >
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>

                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                Page {currentPage + 1} of {totalPages || 1}
                            </span>
                        </Button>

                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                        >
                            Next
                        </Button>
                    </div>
                </div>}
            </div>
        </>
    );
};

export default NewLeadsPage;
